<template>
    <div class="student-portal-certificates" :class="{ dark: dark }">
        <v-container fluid class="pa-4">
            <v-row class="">
                <v-col cols="12">
                    <!-- Header -->
                    <div class="text-left mb-8">
                        <h1 class="card-title">Meus Certificados</h1>
                        <p class="card-subtitle">
                            Visualize e baixe os certificados dos cursos que
                            você concluiu
                        </p>
                    </div>

                    <!-- Loading -->
                    <v-card
                        v-if="isLoading"
                        class="portal-certificates__card pa-8"
                    >
                        <v-row justify="center">
                            <v-col cols="auto">
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                    size="64"
                                ></v-progress-circular>
                                <p class="text-center mt-4">
                                    Carregando certificados...
                                </p>
                            </v-col>
                        </v-row>
                    </v-card>

                    <!-- Certificates Table -->
                    <v-card
                        v-else
                        elevation="2"
                        class="portal-certificates__card p-2 p-md-4"
                    >
                        <v-data-table
                            :headers="headers"
                            :items="dataCertificates"
                            :items-per-page="itemsPerPage"
                            :hide-default-footer="shouldHidePagination"
                            class="certificates-table elevation-0"
                            no-data-text="Nenhum certificado disponível"
                        >
                            <!-- Course Column -->
                            <template v-slot:item.course="{ item }">
                                <div class="d-flex align-center py-2">
                                    <v-avatar
                                        v-if="item.courseImagePathFinal"
                                        size="60"
                                        rounded="lg"
                                        class="mr-4 d-none d-md-block"
                                    >
                                        <v-img
                                            :src="item.courseImagePathFinal"
                                            :alt="item.courseName"
                                            cover
                                        ></v-img>
                                    </v-avatar>
                                    <div>
                                        <div
                                            class="font-weight-medium text-body-1 text-left"
                                        >
                                            {{ item.courseName }}
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- Completion Date Column -->
                            <template v-slot:item.completionDate="{ item }">
                                <div
                                    class="font-weight-medium d-none d-md-block"
                                >
                                    {{ formatDate(item.dateCertificate) }}
                                </div>
                            </template>

                            <!-- Actions Column -->
                            <template v-slot:item.actions="{ item }">
                                <v-btn
                                    color="#19c7d0"
                                    variant="elevated"
                                    prepend-icon="mdi-certificate"
                                    @click="viewCertificate(item.courseId)"
                                    :loading="isLoading"
                                >
                                    Ver Certificado
                                </v-btn>
                            </template>

                            <!-- Empty State -->
                            <template v-slot:no-data>
                                <div class="text-center pa-8">
                                    <v-icon
                                        size="80"
                                        color="grey-lighten-1"
                                        class="mb-4"
                                    >
                                        mdi-school
                                    </v-icon>
                                    <h3 class="text-h5 mb-2">
                                        Nenhum certificado disponível
                                    </h3>
                                    <p class="text-body-1 text-medium-emphasis">
                                        Complete seus cursos para gerar
                                        certificados
                                    </p>
                                </div>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import apiCourses from '@/utils/portal/course.js';
    import apiCertificates from '@/utils/portal/certificate.js';
    import { toast } from 'vue3-toastify';

    export default {
        name: 'StudentCertificates',
        data() {
            return {
                isLoading: false,
                dataCourses: [],
                dataCertificates: [],
                itemsPerPage: 10,
                headers: [
                    {
                        title: 'Curso',
                        key: 'course',
                        sortable: false,
                        width: '40%',
                        minWidth: 200,
                        align: 'start',
                    },
                    {
                        title: 'Data de Conclusão',
                        key: 'completionDate',
                        sortable: true,
                        width: '20%',
                        align: 'center',
                        class: 'd-none d-md-table-cell',
                    },
                    {
                        title: 'Ações',
                        key: 'actions',
                        sortable: false,
                        width: '25%',
                        align: 'center',
                    },
                ],
            };
        },
        computed: {
            ...mapState(['token', 'dark']),
            completedCourses() {
                return this.dataCourses
                    .filter((course) => {
                        // Verifica se o curso está concluído (ajuste conforme estrutura real da API)
                        return (
                            course.course.certificate === 1 ||
                            course.course.status === 1
                        );
                    })
                    .map((course) => ({
                        ...course.course,
                        // Dados mockados que não existem na API
                        completionDate: this.generateMockCompletionDate(),
                        workload: this.generateMockWorkload(),
                    }));
            },
            shouldHidePagination() {
                return this.dataCertificates.length <= this.itemsPerPage;
            },
        },
        created() {
            this.loadCertificates();
        },
        methods: {
            async loadCertificates() {
                try {
                    this.isLoading = true;
                    const certificates =
                        await apiCertificates.getPortalCertificates(this.token);
                    this.dataCertificates = certificates;
                } catch (error) {
                    toast.error('Erro ao carregar os certificados.');
                    throw error;
                } finally {
                    this.isLoading = false;
                }
            },

            viewCertificate(courseId) {
                // Navega para a rota do certificado com o ID do curso
                this.$router.push(`/aluno/certificado/${courseId}`);
            },

            formatDate(date) {
                if (!date) return 'Data não disponível';

                try {
                    const dateObj = new Date(date);
                    return dateObj.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    });
                } catch (error) {
                    return 'Data inválida';
                }
            },
        },
    };
</script>

<style scoped>
    .student-portal-certificates {
        background-color: #f9f9f9fc;
        min-height: calc(100vh - 185px);
    }

    @media screen and (min-width: 960px) {
        .student-portal-certificates {
            padding: 30px 100px;
        }

        .student-portal-certificates {
            min-height: calc(100vh - 120px);
        }
    }

    @media (max-width: 959px) {
        :deep(.certificates-table th:nth-child(2)),
        :deep(.certificates-table td:nth-child(2)) {
            display: none !important;
        }
    }

    .dark.student-portal-certificates {
        background-color: #272727;
    }

    .dark.student-portal-certificates .portal-certificates__card {
        background-color: #121212;
        color: #ffffff;
    }

    .dark.student-portal-certificates .certificates-table {
        background-color: #121212;
        color: #ffffff;
    }
</style>
